import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import {
	CloseButton,
	Combobox,
	InputBase,
	type MantineSize,
	rem,
	useCombobox,
} from '@mantine/core';
import { type CSSProperties, type ReactNode, useState } from 'react';

export interface CreatableSelectItem {
	value: string;
	label: string;
	/** Marks a not-yet-persisted option; rendered with a "(new)" suffix. */
	isNew?: boolean;
}

interface Props {
	value: string | null;
	onChange: (value: string | null) => void;
	data: CreatableSelectItem[];
	/** Called when the "create" option is submitted; return the value to select. */
	onCreate: (query: string) => CreatableSelectItem | string | void;
	getCreateLabel?: (query: string) => ReactNode;
	/** Defaults to: non-empty query with no exact label match. */
	shouldCreate?: (query: string, data: CreatableSelectItem[]) => boolean;
	label?: ReactNode;
	placeholder?: string;
	size?: MantineSize;
	disabled?: boolean;
	clearable?: boolean;
	required?: boolean;
	error?: ReactNode;
	onBlur?: () => void;
	w?: CSSProperties['width'];
	'data-testid'?: string;
}

const CREATE_VALUE = '$create';

/**
 * v7 Combobox-based replacement for the removed `creatable` Select. Controlled
 * via value/onChange; the "create new" option calls `onCreate(query)` and
 * selects whatever value it returns. Search happens inline in the field (like
 * the v6 creatable Select) — no separate search row in the dropdown.
 */
export const CreatableSelect = ({
	value,
	onChange,
	data,
	onCreate,
	getCreateLabel,
	shouldCreate,
	label,
	placeholder,
	size = 'md',
	disabled,
	clearable,
	required,
	error,
	onBlur,
	w,
	'data-testid': dataTestId,
}: Props) => {
	const combobox = useCombobox({
		onDropdownClose: () => combobox.resetSelectedOption(),
	});

	const selectedLabel = data.find((item) => item.value === value)?.label ?? value ?? '';

	// `search` holds the live text while the field is being edited; when not
	// editing the input simply reads the selected label, so it behaves like a
	// plain Select until the user starts typing.
	const [editing, setEditing] = useState(false);
	const [search, setSearch] = useState('');

	const trimmed = search.trim();
	// Show every option until the user actually narrows it (focusing alone, or a
	// query equal to the current selection, keeps the full list visible).
	const showAll = !editing || trimmed.length === 0 || search === selectedLabel;
	const filtered = showAll
		? data
		: data.filter((item) => item.label.toLowerCase().includes(trimmed.toLowerCase()));

	const canCreate = shouldCreate
		? shouldCreate(search, data)
		: trimmed.length > 0 && !data.some((item) => item.label === trimmed);

	return (
		<Combobox
			store={combobox}
			withinPortal
			zIndex={Z_INDEX.GLOBAL_MENU}
			styles={(theme) => ({
				option: {
					padding: '0.25rem',
					fontSize: rem('14px'),
					lineHeight: rem('20px'),
					color: theme.colors.dark[8],
					'&:hover': { backgroundColor: theme.colors.gray[0] },
					'&[data-combobox-selected]': { backgroundColor: theme.colors.gray[0] },
					// The currently-selected value is marked `active` below, mirroring
					// the native Select's `[data-checked]` blue highlight.
					'&[data-combobox-active]': {
						backgroundColor: theme.colors.blue[0],
						border: `1px solid ${theme.colors.blue[2]}`,
						color: theme.colors.dark[8],
						fontWeight: 600,
					},
				},
			})}
			onOptionSubmit={(submitted) => {
				if (submitted === CREATE_VALUE) {
					const created = onCreate(search);
					const newValue = typeof created === 'string' ? created : created?.value;
					if (newValue != null) {
						onChange(newValue);
					}
				} else {
					onChange(submitted);
				}
				setEditing(false);
				combobox.closeDropdown();
			}}
		>
			<Combobox.Target>
				<InputBase
					w={w}
					label={label}
					error={error}
					size={size}
					disabled={disabled}
					required={required}
					data-testid={dataTestId}
					value={editing ? search : selectedLabel}
					placeholder={placeholder}
					rightSection={
						clearable && value ? (
							<CloseButton
								size="sm"
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => onChange(null)}
								aria-label="Clear"
							/>
						) : (
							<Icon variant="caret-down" size="lg" />
						)
					}
					rightSectionPointerEvents={clearable && value ? 'all' : 'none'}
					onChange={(event) => {
						setEditing(true);
						setSearch(event.currentTarget.value);
						combobox.openDropdown();
						combobox.updateSelectedOptionIndex();
					}}
					onClick={() => combobox.openDropdown()}
					onFocus={() => {
						setEditing(true);
						setSearch(selectedLabel);
						combobox.openDropdown();
					}}
					onBlur={() => {
						setEditing(false);
						combobox.closeDropdown();
						onBlur?.();
					}}
				/>
			</Combobox.Target>
			<Combobox.Dropdown>
				<Combobox.Options>
					{filtered.map((item) => (
						<Combobox.Option
							value={item.value}
							key={item.value}
							active={item.value === value}
						>
							{item.isNew ? `${item.label} (new)` : item.label}
						</Combobox.Option>
					))}
					{canCreate && (
						<Combobox.Option value={CREATE_VALUE}>
							{getCreateLabel ? getCreateLabel(search) : `+ Create "${search}"`}
						</Combobox.Option>
					)}
					{filtered.length === 0 && !canCreate && (
						<Combobox.Empty>Nothing found</Combobox.Empty>
					)}
				</Combobox.Options>
			</Combobox.Dropdown>
		</Combobox>
	);
};
